import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AdjustmentState } from '../../types/closing-adjustments.types';

/**
 * The status chip of a correction booking — ADR-0015 §2 / §9.
 *
 * ⚠️ **The one thing BOTH lists share besides the row type** (§9). The closing-wide list (REV-21370)
 * and the in-account list (REV-21364) are two components with different columns, different controls
 * and different widths; the STATUS is the same fact on both, and a user switching between them has to
 * see the same state of the same booking. A second rendering is how the two start disagreeing.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * **Three states, and the middle one is the point** (§2):
 *
 *   `Offen`       — entered, not yet handed to the ERP;
 *   `Übertragen`  — handed to the ERP, **not yet confirmed in the booking journal**;
 *   `Gebucht`     — found again in the imported journal of the ERP.
 *
 * ⚠️ `Übertragen` is a REGULAR state, never an error and never a pending failure: the ERP may simply
 * not have posted yet, and the journal import is what will say. Rendering it in a warning tone would
 * turn the honest middle of the flow into something that looks broken (AC13).
 *
 * ⚠️ The prototype's closing-wide list only knows `Offen`/`Gebucht`. That is a gap in the prototype
 * and the ADR overrules it: without the middle state a correction that has been handed to the ERP is
 * indistinguishable from one nobody has touched.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ **The state is never carried by colour alone** (AC13 of REV-21370, AC9 of REV-21364). Every chip
 * renders its own text, and the tooltip adds the sentence that explains it. A viewer who cannot
 * distinguish the three tints reads three different words.
 */

/** The prototype's badge tints. Names, not colours — the stylesheet owns the palette. */
type StatusTone = 'proposed' | 'released' | 'transferred' | 'booked';

const STATE_TONES: Record<AdjustmentState, StatusTone> = {
    proposed: 'proposed',
    released: 'released',
    transferred: 'transferred',
    booked: 'booked',
};

const STATE_LABEL_KEYS: Record<AdjustmentState, string> = {
    // Declared because the type requires it; unreachable until an agent proposes corrections.
    proposed: 'abcp_adjustments_state_proposed',
    released: 'abcp_adjustments_state_released',
    transferred: 'abcp_adjustments_state_transferred',
    booked: 'abcp_adjustments_state_booked',
};

/**
 * The tooltip per state, and it is not decoration.
 *
 * `Übertragen` in particular has to SAY that the proof is still outstanding — the prototype's own
 * progress display already words it ("Buchungsnachweis folgt mit «ERP aktualisieren»"), and §2 makes
 * that text mandatory rather than optional.
 */
const STATE_HINT_KEYS: Record<AdjustmentState, string> = {
    proposed: 'abcp_adjustments_state_proposed_hint',
    released: 'abcp_adjustments_state_released_hint',
    transferred: 'abcp_adjustments_state_transferred_hint',
    booked: 'abcp_adjustments_state_booked_hint',
};

@Component({
    selector: 'abcp-adjustment-status',
    templateUrl: './adjustment-status.component.html',
    styleUrls: ['./adjustment-status.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpAdjustmentStatusComponent {
    @Input() state: AdjustmentState = 'released';

    /**
     * A PRIOR-year row: the chip then reads `Gebucht <Jahr>` (§10).
     *
     * The year rather than a boolean, because the label carries it. `null` is the current year.
     */
    @Input() bookedYear: number | null = null;

    /**
     * Several journal lines matched and nothing was guessed (§4).
     *
     * Rendered as a SECOND, separate marking beside the state rather than as a fourth state: the
     * correction really is `Übertragen`, and it additionally has an open question. Folding the two into
     * one chip would lose the state.
     */
    @Input() ambiguous = false;

    /**
     * The reconciliation looked and did not find this booking in the journal (V2/AC6).
     *
     * A SECOND badge beside the state, exactly like `ambiguous` and for the same reason: the
     * correction really is `Übertragen`, and this says why it has stayed that way. Without it a user
     * waits for a confirmation that is never coming, with nothing on screen to explain the wait.
     */
    @Input() notFound = false;

    /** `sm` for the narrow in-account pane, `md` for the closing-wide register. */
    @Input() size: 'sm' | 'md' = 'md';

    get tone(): StatusTone {
        return STATE_TONES[this.state];
    }

    get labelKey(): string {
        return STATE_LABEL_KEYS[this.state];
    }

    get hintKey(): string {
        return STATE_HINT_KEYS[this.state];
    }

    /** A prior-year row appends its year — `Gebucht 2025`. Only ever on `booked`. */
    get yearSuffix(): string {
        return this.state === 'booked' && this.bookedYear !== null ? ` ${String(this.bookedYear)}` : '';
    }
}
